/**
 * Os diálogos dos testes: "Ver resultado" (a MESMA visão da tela final), "Ver respostas" e "Anular" (motivo
 * obrigatório). Todos com o `Dialog` canônico; textos literais do conteúdo aprovado.
 */
import { useEffect, useState } from 'react';
import { Alert, Button, Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, Input, Label, Select, SelectOption } from '@/components/ui';
import { CAMPO_VERSAO_DO_TESTE, VERSOES_DO_QA, type GerenciaQa } from '@/lib/testes/versao-qa';
import { ResultadoTeste } from '@/components/testes/ResultadoTeste';
import { formatDate } from '@/lib/format-date';
import { buscarRespostas, baixarPdfResultado } from '@/lib/testes/testes-store';
import { nomeDoTeste, preencher, TEXTOS, tituloDoResultadoParaGestor } from '@/lib/testes/textos';
import type { RespostasDetalhadas, ResultadoDoFuncionario } from '@/lib/testes/tipos';
import { errMsg } from '@/lib/api-error';

const F = TEXTOS.ficha;
const LP = TEXTOS.link_publico;

/**
 * `primeiroNomeDoFuncionario` vem SÓ da visão de gestão (diretoria/líder): aí o título é "Resultado … de {nome}" (A1,
 * FEEDBACK-CARREIRA). Sem ele é o próprio funcionário, que continua lendo "Seu resultado".
 */
export function VerResultadoDialog({ resultado, onClose, primeiroNomeDoFuncionario }: { resultado: ResultadoDoFuncionario; onClose: () => void; primeiroNomeDoFuncionario?: string }) {
  const { testeMinusculo } = nomeDoTeste(resultado.tipo);
  const titulo = primeiroNomeDoFuncionario
    ? tituloDoResultadoParaGestor(resultado.tipo, primeiroNomeDoFuncionario)
    : preencher(LP.reaberto_titulo, { teste_minusculo: testeMinusculo });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()} size="lg">
      <DialogHeader>
        <DialogTitle>{titulo}</DialogTitle>
      </DialogHeader>
      <DialogContent>
        <p className="mb-4 text-sm text-ink-2 dark:text-ink-dark-2">{preencher(LP.reaberto_intro, { data: formatDate(resultado.concluidoEmIso) })}</p>
        <ResultadoTeste visao={resultado.visao} paraGestor={!!primeiroNomeDoFuncionario} />
      </DialogContent>
      <DialogFooter>
        <Button variant="secondary" onClick={() => void baixarPdfResultado(resultado.id)}>
          {F.botao_pdf}
        </Button>
        <Button onClick={onClose}>{LP.botao_voltar}</Button>
      </DialogFooter>
    </Dialog>
  );
}

export function VerRespostasDialog({ resultado, nomeCompleto, onClose }: { resultado: ResultadoDoFuncionario; nomeCompleto: string; onClose: () => void }) {
  const [dados, setDados] = useState<RespostasDetalhadas | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  useEffect(() => {
    buscarRespostas(resultado.id).then(setDados).catch((e) => setErro(errMsg(e)));
  }, [resultado.id]);
  const { teste } = nomeDoTeste(resultado.tipo);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()} size="lg">
      <DialogHeader>
        <DialogTitle>{preencher(TEXTOS.pdf.titulo_respostas, { teste, nome_completo: nomeCompleto })}</DialogTitle>
      </DialogHeader>
      <DialogContent>
        {erro && <Alert variant="danger">{erro}</Alert>}
        {!dados && !erro && <div className="h-24 animate-pulse rounded bg-paper-warm dark:bg-paper-dark-warm" aria-busy="true" />}
        {dados?.tipo === 'disc' && (
          <ol className="divide-y divide-line text-sm dark:divide-line-dark" data-testid="respostas-disc">
            {dados.grupos.map((g) => (
              <li key={g.n} className="flex flex-wrap gap-x-4 gap-y-1 py-2">
                <span className="w-28 shrink-0 whitespace-nowrap text-ink-3">{preencher(LP.contador_disc, { n: g.n })}</span>
                <span className="text-ink-1 dark:text-ink-dark-1">
                  <span className="font-semibold text-success">{LP.botao_mais}:</span> {g.mais}
                </span>
                <span className="text-ink-1 dark:text-ink-dark-1">
                  <span className="font-semibold text-red">{LP.botao_menos}:</span> {g.menos}
                </span>
              </li>
            ))}
          </ol>
        )}
        {dados?.tipo === 'qa' && (
          <ol className="divide-y divide-line text-sm dark:divide-line-dark" data-testid="respostas-qa">
            {dados.situacoes.map((s) => (
              <li key={s.n} className="flex gap-3 py-2">
                <span className="w-8 shrink-0 text-right text-lg font-semibold tabular-nums text-ink-1 dark:text-ink-dark-1">{s.nota}</span>
                <span className="text-ink-1 dark:text-ink-dark-1">
                  <span className="block text-xs text-ink-3">
                    {preencher(LP.contador_qa, { n: s.n })} · {s.medida}
                  </span>
                  {s.situacao}
                </span>
              </li>
            ))}
          </ol>
        )}
      </DialogContent>
      <DialogFooter>
        <Button onClick={onClose}>{LP.botao_voltar}</Button>
      </DialogFooter>
    </Dialog>
  );
}

export function AnularDialog({ onConfirm, onClose }: { onConfirm: (motivo: string) => Promise<void>; onClose: () => void }) {
  const [motivo, setMotivo] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()} size="sm">
      <DialogHeader>
        <DialogTitle>{F.anular_titulo}</DialogTitle>
      </DialogHeader>
      <DialogContent>
        <p className="mb-3 text-sm text-ink-2 dark:text-ink-dark-2">{F.anular_aviso}</p>
        <Label htmlFor="anular-motivo">{F.anular_motivo}</Label>
        <Input
          id="anular-motivo"
          value={motivo}
          autoFocus
          maxLength={500}
          aria-invalid={!!erro}
          onChange={(e) => {
            setMotivo(e.target.value);
            setErro(null);
          }}
          data-testid="anular-motivo"
        />
        {erro && <p className="mt-1 text-sm text-red">{erro}</p>}
      </DialogContent>
      <DialogFooter>
        <Button variant="secondary" onClick={onClose}>
          {LP.botao_voltar}
        </Button>
        <Button
          variant="destructive"
          disabled={ocupado}
          data-testid="anular-confirmar"
          onClick={async () => {
            if (!motivo.trim()) {
              setErro(F.anular_motivo);
              return;
            }
            setOcupado(true);
            try {
              await onConfirm(motivo.trim());
              onClose();
            } catch (e) {
              setErro(errMsg(e));
            } finally {
              setOcupado(false);
            }
          }}
        >
          {F.botao_anular}
        </Button>
      </DialogFooter>
    </Dialog>
  );
}

/**
 * QA por gerência (05/10/2026): ficha sem versão automática (sem área, ou a área Diretoria) → quem envia escolhe a
 * versão. Textos: o rótulo e as opções são literais do pedido; título e botões reaproveitam os da aba.
 */
export function VersaoDoQaDialog({ onConfirm, onClose }: { onConfirm: (g: GerenciaQa) => void; onClose: () => void }) {
  const [valor, setValor] = useState<GerenciaQa | ''>('');
  const { teste } = nomeDoTeste('qa');
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()} size="sm">
      <DialogHeader>
        <DialogTitle>{preencher(F.botao_enviar, { teste })}</DialogTitle>
      </DialogHeader>
      <DialogContent>
        <Label htmlFor="versao-do-qa">{CAMPO_VERSAO_DO_TESTE}</Label>
        <Select id="versao-do-qa" value={valor} onChange={(e) => setValor(e.target.value as GerenciaQa)} data-testid="versao-do-qa" wrapperClassName="w-full">
          <SelectOption value="">—</SelectOption>
          {VERSOES_DO_QA.map((v) => (
            <SelectOption key={v.valor} value={v.valor}>
              {v.rotulo}
            </SelectOption>
          ))}
        </Select>
      </DialogContent>
      <DialogFooter>
        <Button variant="secondary" onClick={onClose}>
          {LP.botao_voltar}
        </Button>
        <Button disabled={!valor} onClick={() => valor && onConfirm(valor)} data-testid="versao-do-qa-confirmar">
          {preencher(F.botao_enviar, { teste })}
        </Button>
      </DialogFooter>
    </Dialog>
  );
}
